import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { HeaderState } from "src/app/models/header/HeaderState";
import { ActionService } from "src/app/services/Action.service";
import { DataService } from "src/app/services/Data.service";
import {
  HeaderActionsService,
  HeaderActionType,
} from "src/app/services/header-actions.service";
import { PdfService } from "src/app/services/pdf.service";
import { SaveSimulationForm } from "src/app/shared/components/header-modals/save-simulation-modal/save-simulation-modal.component";
import { SaveSimulationService } from "src/app/shared/components/header-modals/save-simulation-modal/save-simulation.service";
import { PreSimulationService } from "src/app/shared/components/pre-simulation/service/pre-simulation.service";
import { NAVIGATIONS } from "src/app/shared/consts/navigations";
import { UnitSystem } from "src/app/shared/enums/unit-system.enum";
import {
  UnitConversionService,
  UNITS,
} from "src/app/shared/services/unit-conversion.service";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";
import { SlottedModalComponent } from "../slotted-perforated-pipe/shared/slotted-modal/slotted-modal.component";
import {
  GEOTHERMAL_LUS_COEFF_DEFAULTS,
  GEOTHERMAL_TEMP_T_DEFAULTS,
  getGeothermalFormDefaults,
} from "./geothermal-defaults";
import {
  GEOTHERMAL_DECIMAL_PLACES_VALIDATORS,
  getDecimalPattern,
  getMaxMinValidator,
} from "./geothermal-validators.constants";
import { GeothermalService } from "./services/geothermal.service";
import { normalizeGeothermalResults } from "./utils/geothermal-adapter";

@Component({
  selector: "app-geothermal",
  templateUrl: "./geothermal.component.html",
  styleUrls: ["./geothermal.component.css"],
})
export class GeothermalComponent implements OnInit, OnDestroy {
  showThermalCollapseResults = false;
  showUnsupportedBucklingResults = false;
  @ViewChild("outsideDiameterInput")
  outsideDiameterInputRef: ElementRef<HTMLInputElement>;

  private originalFormValue: any;
  private defaultFormValue: any;
  geothermalResults: any = null;
  public readonly UnitSystem = UnitSystem;
  // Avoid clearing results while patching values during Load Simulation
  private isPatchingFromLoad: boolean = false;
  private currentTreeId: number | null = null;
  unitSystem: UnitSystem = UnitSystem.USC;
  isRangeModalOpen = false;
  isSaveModalOpen = false;
  @ViewChild(SlottedModalComponent)
  slottedModalComponent: SlottedModalComponent;
  @ViewChild("geoFormElem") geoFormElem!: ElementRef<HTMLFormElement>;
  readonly tempTDefaults = GEOTHERMAL_TEMP_T_DEFAULTS;
  readonly directionMotionOptions = [
    { label: "DOWN", value: "DOWN" },
    { label: "UP", value: "UP" },
  ];
  lusCoeffLabels = Array.from({ length: 12 }, (_, i) => i + 1);
  readonly lusCoeffDefaults = GEOTHERMAL_LUS_COEFF_DEFAULTS;
  imgModelError: string | null = null;
  imgPHError: string | null = null;
  isPreSimulationFormValid: boolean = false;
  customerId: number | null;
  countryId: number | null;

  readonly NAVIGATIONS = NAVIGATIONS;

  subActionService: Subscription;
  simulationName: string = "";
  fgGeothermal: UntypedFormGroup;
  simulationRev: number = 1;
  validReportGEO: boolean = false;
  department: string = "";
  nameCurrentUser: string;
  isUser: boolean = true;
  dcpProduct$: Subscription;
  headerSubscription: Subscription;

  // Solo se usa rawByAttrAndValue para cacheo y conversión
  canonicalSI: { [key: string]: number | null } = {};
  canonicalSubs: Subscription[] = [];
  isProgrammaticPatch = false;

  // Cache for attribute-value conversions (used in getOrConvertValue)
  rawByAttrAndValue: { [attrName: string]: { [value: string]: any } } = {};

  constructor(
    private preSimulationService: PreSimulationService,
    private saveSimulationService: SaveSimulationService,
    private actionService: ActionService,
    private dataService: DataService,
    private pdfService: PdfService,
    private fb: UntypedFormBuilder,
    private geothermalService: GeothermalService,
    public unitService: UnitConversionService,
    private headerActionsService: HeaderActionsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.dataService.changeModulo("geothermal");
    this.subscribeActionService();

    this.headerSubscription =
      this.headerActionsService.actionRequested$.subscribe((action) => {
        switch (action.type) {
          case HeaderActionType.Save:
            if (this.canSaveSimulation()) {
              this.handleSaveSimulation(action.payload);
            }
            break;
          case HeaderActionType.Load:
            this.handleLoadSimulation(action.payload);
            break;
          case HeaderActionType.Delete:
            // If the deleted simulation is the currently loaded one, reset the form
            if (
              action.payload &&
              this.currentTreeId &&
              action.payload.simulation.id === this.currentTreeId
            ) {
              this.resetSimulation();
              this.preSimulationService.resetValues();
            }
            break;
          case HeaderActionType.ValidatePrintPdf:
            if (this.canPrintReport()) {
              this.refreshSimulationContext();
              this.headerActionsService.requestAction(
                HeaderActionType.OpenPrintPdfModal,
              );
            }
            break;
          case HeaderActionType.PrintPdf:
            this.printPdfReport(action.payload);
            break;
          case HeaderActionType.ValidatePrintCsv:
            if (this.canPrintReport()) {
              this.headerActionsService.requestAction(
                HeaderActionType.PrintCsv,
              );
            }
            break;
          case HeaderActionType.PrintCsv:
            this.printCsvReport();
            break;
        }
      });

    this.department = this.dataService.getCurrentDepartment();
    this.nameCurrentUser = this.dataService.getCurrentNameSurname();

    this.preSimulationService.formValidity$.subscribe((isValid) => {
      setTimeout(() => {
        this.isPreSimulationFormValid = isValid;
      });
    });

    this.setRol();

    this.createForm();

    // Initialize canonical store and observers after the form exists
    this.initializeCanonicalFromForm();
    this.setupCanonicalObservers();

    // Subscriptions that require the form to exist
    this.dcpProduct$ = this.geothermalService.dcpProduct$.subscribe(
      (dcpProduct) => {
        if (!this.fgGeothermal) return;

        // Don't override values during LoadSimulation patching
        if (this.isPatchingFromLoad) return;

        this.fgGeothermal
          .get("outsideDiameter")
          ?.setValue(
            dcpProduct.outsideDiameter != null
              ? Number(dcpProduct.outsideDiameter).toFixed(
                  GEOTHERMAL_DECIMAL_PLACES_VALIDATORS["outsideDiameter"][
                    this.unitSystem
                  ],
                )
              : null,
          );
        this.fgGeothermal
          .get("nominalWeight")
          ?.setValue(
            dcpProduct.nominalWeight != null
              ? Number(dcpProduct.nominalWeight).toFixed(
                  GEOTHERMAL_DECIMAL_PLACES_VALIDATORS["nominalWeight"][
                    this.unitSystem
                  ],
                )
              : null,
          );
        this.fgGeothermal
          .get("wallThickness")
          ?.setValue(
            dcpProduct.wallThickness != null
              ? Number(dcpProduct.wallThickness).toFixed(
                  GEOTHERMAL_DECIMAL_PLACES_VALIDATORS["wallThickness"][
                    this.unitSystem
                  ],
                )
              : null,
          );
        this.fgGeothermal.get("grade")?.setValue(dcpProduct.grade);
        this.fgGeothermal.get("smys")?.setValue(
          dcpProduct.minYieldStrength != null
            ? (this.unitSystem === UnitSystem.SI
                ? Number(dcpProduct.minYieldStrength) / 1000 // SI Unit: kPa, MPa is required.
                : Number(dcpProduct.minYieldStrength)
              ).toFixed(
                GEOTHERMAL_DECIMAL_PLACES_VALIDATORS["smys"][this.unitSystem],
              )
            : null,
        );
        this.geothermalService.setCloseDcpModal(true);
      },
    );

    this.configFormChanges();
  }

  private setRol() {
    const roles = this.dataService.getCurrentRol();
    if (roles && roles.some((x) => x == "ADMIN")) {
      this.isUser = false;
    } else if (roles && roles.some((x) => x == "SUPER_USER")) {
      this.isUser = false;
    } else if (roles && roles.some((x) => x == "USER")) {
      this.isUser = true;
    }
  }

  private configFormChanges() {
    this.fgGeothermal?.get("outsideDiameter")?.valueChanges.subscribe(() => {
      this.updateInsideDiameter();
      this.updateHoleIdValidators();
    });

    this.fgGeothermal?.get("wallThickness")?.valueChanges.subscribe(() => {
      this.updateInsideDiameter();
    });

    this.fgGeothermal?.valueChanges.subscribe(() => {
      if (!this.isPatchingFromLoad) {
        this.geothermalResults = null;
        this.showThermalCollapseResults = false;
        this.showUnsupportedBucklingResults = false;
      }
    });
  }

  onUnitSystemChange(unit: UnitSystem) {
    const prevUnit = this.unitSystem;
    if (this.fgGeothermal) {
      const controls = this.getConvertibleControls();
      controls.forEach((key) => {
        const ctrl = this.fgGeothermal.get(key);
        if (!ctrl) return;
        const val = ctrl.value;

        // Detect if the original value is in scientific notation
        const isScientific = typeof val === "string" && /e[-+]?\d+/i.test(val);
        let converted = this.getOrConvertValue(
          key,
          val,
          unit,
          (value, unitSystem) => {
            const fromUnit = this.getUnitForControl(key, prevUnit);
            const toUnit = this.getUnitForControl(key, unitSystem);
            var newValue = this.convertValue(value, fromUnit, toUnit);
            return newValue;
          },
        );
        // If the original value was in scientific notation, display the converted value in the same format
        if (isScientific && typeof converted === "number") {
          converted = converted.toExponential(2);
        }

        setTimeout(() => {
          ctrl.setValue(converted, { emitEvent: false });
          ctrl.updateValueAndValidity();
        }, 0);
      });
      this.cdr.detectChanges();
    }

    this.unitSystem = unit;
    this.geothermalService.setUnitSystem(unit);
    const filterValues = {
      outsideDiameter: null,
      wallThickness: null,
      nominalWeight: null,
      grade: null,
      connection: null,
      minWallThickness: null,
    };
    this.slottedModalComponent.preloadFilters(
      filterValues,
      this.unitSystem,
      true,
    );
    // Conversion is handled via canonical stores to avoid cumulative rounding

    const controls = this.fgGeothermal.controls;

    // Eccentricity
    if (controls["eccentricity"]) {
      controls["eccentricity"].setValidators([
        Validators.required,
        Validators.pattern(getDecimalPattern("eccentricity", this.unitSystem)),
      ]);
      controls["eccentricity"].updateValueAndValidity();
    }
    // Modulus of elasticity (psi <-> MPa)
    if (controls["modulusE"]) {
      let min = getMaxMinValidator("MODULUS_E_MIN", this.unitSystem);
      let max = getMaxMinValidator("MODULUS_E_MAX", this.unitSystem);
      controls["modulusE"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("modulusE", this.unitSystem)),
      ]);
      controls["modulusE"].updateValueAndValidity();
    }
    // Coef. thermal exp (%/°C <-> %/°F)
    if (controls["thermalExp"]) {
      let min = getMaxMinValidator("THERMAL_EXP_MIN", this.unitSystem);
      let max = getMaxMinValidator("THERMAL_EXP_MAX", this.unitSystem);
      controls["thermalExp"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
      ]);
      controls["thermalExp"].updateValueAndValidity();
    }
    // Outside Diameter (in <-> mm)
    if (controls["outsideDiameter"]) {
      let min = getMaxMinValidator("OUTSIDE_DIAMETER_MIN", this.unitSystem);
      let max = getMaxMinValidator("OUTSIDE_DIAMETER_MAX", this.unitSystem);
      controls["outsideDiameter"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(
          getDecimalPattern("outsideDiameter", this.unitSystem),
        ),
      ]);
      controls["outsideDiameter"].updateValueAndValidity();
    }
    // Nominal Weight (lb/ft <-> kg/m)
    if (controls["nominalWeight"]) {
      let min = getMaxMinValidator("NOMINAL_WEIGHT_MIN", this.unitSystem);
      let max = getMaxMinValidator("NOMINAL_WEIGHT_MAX", this.unitSystem);
      controls["nominalWeight"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("nominalWeight", this.unitSystem)),
      ]);
      controls["nominalWeight"].updateValueAndValidity();
    }
    // Wall Thickness (in <-> mm)
    if (controls["wallThickness"]) {
      controls["wallThickness"].setValidators([
        Validators.required,
        Validators.pattern(getDecimalPattern("wallThickness", this.unitSystem)),
      ]);
      controls["wallThickness"].updateValueAndValidity();
    }
    // Inside Diameter (in <-> mm)
    if (controls["insideDiameter"]) {
      let min = getMaxMinValidator("INSIDE_DIAMETER_MIN", this.unitSystem);
      let max = getMaxMinValidator("INSIDE_DIAMETER_MAX", this.unitSystem);
      controls["insideDiameter"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(
          getDecimalPattern("insideDiameter", this.unitSystem),
        ),
      ]);
      controls["insideDiameter"].updateValueAndValidity();
    }
    // Grade
    if (controls["grade"]) {
      controls["grade"].setValidators([Validators.required]);
      controls["grade"].updateValueAndValidity();
    }
    // SMYS (psi <-> MPa)
    if (controls["smys"]) {
      let min = getMaxMinValidator("SMYS_MIN", this.unitSystem);
      let max = getMaxMinValidator("SMYS_MAX", this.unitSystem);
      controls["smys"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("smys", this.unitSystem)),
      ]);
      controls["smys"].updateValueAndValidity();
    }
    // Temp Derating
    if (controls["tempDerating"]) {
      let min = getMaxMinValidator("TEMP_DERATING_MIN", this.unitSystem);
      let max = getMaxMinValidator("TEMP_DERATING_MAX", this.unitSystem);
      controls["tempDerating"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("tempDerating", this.unitSystem)),
      ]);
      controls["tempDerating"].updateValueAndValidity();
    }
    // hollLimit
    if (controls["hollLimit"]) {
      let min = getMaxMinValidator("HOLLIMIT_MIN", this.unitSystem);
      let max = getMaxMinValidator("HOLLIMIT_MAX", this.unitSystem);
      controls["hollLimit"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("hollLimit", this.unitSystem)),
      ]);
      controls["hollLimit"].updateValueAndValidity();
    }
    // holeId (in <-> mm)
    if (controls["holeId"]) {
      let min = getMaxMinValidator("HOLE_ID_MIN", this.unitSystem);
      let max = getMaxMinValidator("HOLE_ID_MAX", this.unitSystem);
      controls["holeId"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
      ]);
      controls["holeId"].updateValueAndValidity();
    }
    // Densidades (ppg <-> kg/m3)
    let densMin = getMaxMinValidator("DENSITY_MIN", this.unitSystem);
    let densMax = getMaxMinValidator("DENSITY_MAX", this.unitSystem);
    [
      "insideDensityInitial",
      "insideDensityFinal",
      "outsideDensityInitial",
      "outsideDensityFinal",
    ].forEach((key) => {
      if (controls[key]) {
        controls[key].setValidators([
          Validators.required,
          Validators.min(densMin),
          Validators.max(densMax),
          Validators.pattern(getDecimalPattern(key, this.unitSystem)),
        ]);
        controls[key].updateValueAndValidity();
      }
    });
    // Presiones (psi <-> MPa)
    let presMin = getMaxMinValidator("PRESSURE_MIN", this.unitSystem);
    let presMaxIn = getMaxMinValidator("PRESSURE_MAX_IN", this.unitSystem);
    let presMaxOut = getMaxMinValidator("PRESSURE_MAX_OUT", this.unitSystem);
    ["insidePressureInitial", "insidePressureFinal"].forEach((key) => {
      if (controls[key]) {
        controls[key].setValidators([
          Validators.required,
          Validators.min(presMin),
          Validators.max(presMaxIn),
          Validators.pattern(getDecimalPattern(key, this.unitSystem)),
        ]);
        controls[key].updateValueAndValidity();
      }
    });
    ["outsidePressureInitial", "outsidePressureFinal"].forEach((key) => {
      if (controls[key]) {
        controls[key].setValidators([
          Validators.required,
          Validators.min(presMin),
          Validators.max(presMaxOut),
          Validators.pattern(getDecimalPattern(key, this.unitSystem)),
        ]);
        controls[key].updateValueAndValidity();
      }
    });
    // Temp. (F <-> C)
    let tempMin = getMaxMinValidator("TEMPERATURE_MIN", this.unitSystem);
    let tempMax = getMaxMinValidator("TEMPERATURE_MAX", this.unitSystem);
    ["avgTemperatureInitial", "avgTemperatureFinal"].forEach((key) => {
      if (controls[key]) {
        controls[key].setValidators([
          Validators.required,
          Validators.min(tempMin),
          Validators.max(tempMax),
          Validators.pattern(getDecimalPattern(key, this.unitSystem)),
        ]);
        controls[key].updateValueAndValidity();
      }
    });
    // TempT1–T5 (F <-> C)
    for (let t = 1; t <= 5; t++) {
      const key = `tempT${t}`;
      if (controls[key]) {
        controls[key].setValidators([
          Validators.pattern(getDecimalPattern(key, this.unitSystem)),
        ]);
        controls[key].updateValueAndValidity();
      }
    }
    // deltaLPreload (ft <-> m)
    if (controls["deltaLPreload"]) {
      let min = getMaxMinValidator("DELTA_L_PRELOAD_MIN", this.unitSystem);
      let max = getMaxMinValidator("DELTA_L_PRELOAD_MAX", this.unitSystem);
      controls["deltaLPreload"].setValidators([
        Validators.required,
        Validators.min(min),
        Validators.max(max),
        Validators.pattern(getDecimalPattern("deltaLPreload", this.unitSystem)),
      ]);
      controls["deltaLPreload"].updateValueAndValidity();
    }

    // Derived field depends on OD and WT; recompute after conversion
    this.updateInsideDiameter();
    // Update hole ID validators based on new OD value
    this.updateHoleIdValidators();
  }

  private updateInsideDiameter(respectExplicitValues: boolean = false) {
    // If we should respect explicit values (like during load simulation)
    if (respectExplicitValues) {
      const ctrl = this.fgGeothermal?.get("insideDiameter");
      const currentValue = ctrl?.value;

      // Only respect values that were explicitly set (not undefined)
      // null is considered an explicit value meaning "empty field"
      if (currentValue !== undefined) {
        return;
      }
    }

    const od = this.fgGeothermal?.get("outsideDiameter")?.value;
    const wt = this.fgGeothermal?.get("wallThickness")?.value;
    if (od != null && wt != null && !isNaN(od) && !isNaN(wt)) {
      const id = +(od - 2 * wt).toFixed(3);
      const ctrl = this.fgGeothermal.get("insideDiameter");
      if (ctrl) {
        ctrl.setValue(id, { emitEvent: false });
        ctrl.updateValueAndValidity();
      }
    } else {
      // If we can't calculate (missing OD or WT), clear the field
      const ctrl = this.fgGeothermal.get("insideDiameter");
      if (ctrl && ctrl.value !== null) {
        ctrl.setValue(null, { emitEvent: false });
        ctrl.updateValueAndValidity();
      }
    }
  }

  private updateHoleIdValidators() {
    const holeIdCtrl = this.fgGeothermal?.get("holeId");
    const od = this.fgGeothermal?.get("outsideDiameter")?.value;

    if (holeIdCtrl && od != null && !isNaN(od)) {
      // Set minimum to OD + small increment to ensure it's always greater
      const dynamicMin = Number(od) + 0.001;
      const staticMax = getMaxMinValidator("HOLE_ID_MAX", this.unitSystem);

      holeIdCtrl.setValidators([
        Validators.required,
        Validators.min(dynamicMin),
        Validators.max(staticMax),
        Validators.pattern(getDecimalPattern("holeId", this.unitSystem)),
      ]);
      holeIdCtrl.updateValueAndValidity();
    }
  }

  private createForm() {
    const defaults = getGeothermalFormDefaults(this.unitSystem);
    // General Information controls
    const generalInfoControls = {
      modulusE: [
        defaults.modulusE,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("MODULUS_E_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("MODULUS_E_MAX", this.unitSystem)),
          Validators.pattern(getDecimalPattern("modulusE", this.unitSystem)),
        ],
      ],
      poisson: [
        defaults.poisson,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("POISSON_MIN")),
          Validators.max(getMaxMinValidator("POISSON_MAX")),
          Validators.pattern(getDecimalPattern("poisson", this.unitSystem)),
        ],
      ],
      thermalExp: [
        defaults.thermalExp,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("THERMAL_EXP_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("THERMAL_EXP_MAX", this.unitSystem),
          ),
        ],
      ],
      friction: [
        defaults.friction,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("FRICTION_MIN")),
          Validators.max(getMaxMinValidator("FRICTION_MAX")),
          Validators.pattern(getDecimalPattern("friction", this.unitSystem)),
        ],
      ],
      calcEccentricity: [defaults.calcEccentricity],
      eccentricity: [
        { value: defaults.eccentricity, disabled: true },
        [
          Validators.required,
          Validators.pattern(
            getDecimalPattern("eccentricity", this.unitSystem),
          ),
        ],
      ],
      directionMotion: [defaults.directionMotion, [Validators.required]],
    };

    // Casing Specification controls
    const casingSpecControls = {
      outsideDiameter: [
        defaults.outsideDiameter,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("OUTSIDE_DIAMETER_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("OUTSIDE_DIAMETER_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("outsideDiameter", this.unitSystem),
          ),
        ],
      ],
      nominalWeight: [
        defaults.nominalWeight,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("NOMINAL_WEIGHT_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("NOMINAL_WEIGHT_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("nominalWeight", this.unitSystem),
          ),
        ],
      ],
      wallThickness: [
        defaults.wallThickness,
        [
          Validators.required,
          Validators.pattern(
            getDecimalPattern("wallThickness", this.unitSystem),
          ),
        ],
      ],
      insideDiameter: [
        defaults.insideDiameter,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("INSIDE_DIAMETER_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("INSIDE_DIAMETER_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("insideDiameter", this.unitSystem),
          ),
        ],
      ],
      grade: [defaults.grade, [Validators.required]],
      smys: [
        defaults.smys,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("SMYS_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("SMYS_MAX", this.unitSystem)),
          Validators.pattern(getDecimalPattern("smys", this.unitSystem)),
        ],
      ],
      tempDerating: [
        defaults.tempDerating,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("TEMP_DERATING_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("TEMP_DERATING_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("tempDerating", this.unitSystem),
          ),
        ],
      ],
      hollLimit: [
        defaults.hollLimit,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("HOLLIMIT_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("HOLLIMIT_MAX", this.unitSystem)),
          Validators.pattern(getDecimalPattern("hollLimit", this.unitSystem)),
        ],
      ],
    };

    // Hole Dimension controls
    const holeDimensionControls = {
      holeId: [
        defaults.holeId,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("HOLE_ID_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("HOLE_ID_MAX", this.unitSystem)),
          Validators.pattern(getDecimalPattern("holeId", this.unitSystem)),
        ],
      ],
    };

    // Load Information (Initial and Final Conditions) controls
    const loadInfoControls = {
      insideDensityInitial: [
        defaults.insideDensityInitial,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("DENSITY_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("DENSITY_MAX", this.unitSystem)),
          Validators.pattern(
            getDecimalPattern("insideDensityInitial", this.unitSystem),
          ),
        ],
      ],
      insideDensityFinal: [
        defaults.insideDensityFinal,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("DENSITY_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("DENSITY_MAX", this.unitSystem)),
          Validators.pattern(
            getDecimalPattern("insideDensityFinal", this.unitSystem),
          ),
        ],
      ],
      outsideDensityInitial: [
        defaults.outsideDensityInitial,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("DENSITY_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("DENSITY_MAX", this.unitSystem)),
          Validators.pattern(
            getDecimalPattern("outsideDensityInitial", this.unitSystem),
          ),
        ],
      ],
      outsideDensityFinal: [
        defaults.outsideDensityFinal,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("DENSITY_MIN", this.unitSystem)),
          Validators.max(getMaxMinValidator("DENSITY_MAX", this.unitSystem)),
          Validators.pattern(
            getDecimalPattern("outsideDensityFinal", this.unitSystem),
          ),
        ],
      ],
      insidePressureInitial: [
        defaults.insidePressureInitial,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("PRESSURE_MIN", this.unitSystem)),
          Validators.max(
            getMaxMinValidator("PRESSURE_MAX_IN", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("insidePressureInitial", this.unitSystem),
          ),
        ],
      ],
      insidePressureFinal: [
        defaults.insidePressureFinal,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("PRESSURE_MIN", this.unitSystem)),
          Validators.max(
            getMaxMinValidator("PRESSURE_MAX_IN", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("insidePressureFinal", this.unitSystem),
          ),
        ],
      ],
      outsidePressureInitial: [
        defaults.outsidePressureInitial,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("PRESSURE_MIN", this.unitSystem)),
          Validators.max(
            getMaxMinValidator("PRESSURE_MAX_OUT", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("outsidePressureInitial", this.unitSystem),
          ),
        ],
      ],
      outsidePressureFinal: [
        defaults.outsidePressureFinal,
        [
          Validators.required,
          Validators.min(getMaxMinValidator("PRESSURE_MIN", this.unitSystem)),
          Validators.max(
            getMaxMinValidator("PRESSURE_MAX_OUT", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("outsidePressureFinal", this.unitSystem),
          ),
        ],
      ],
      avgTemperatureInitial: [
        defaults.avgTemperatureInitial,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("TEMPERATURE_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("TEMPERATURE_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("avgTemperatureInitial", this.unitSystem),
          ),
        ],
      ],
      avgTemperatureFinal: [
        defaults.avgTemperatureFinal,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("TEMPERATURE_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("TEMPERATURE_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("avgTemperatureFinal", this.unitSystem),
          ),
        ],
      ],
    };

    // Displacement due to Pre-load controls
    const displacementPreloadControls = {
      deltaLPreload: [
        defaults.deltaLPreload,
        [
          Validators.required,
          Validators.min(
            getMaxMinValidator("DELTA_L_PRELOAD_MIN", this.unitSystem),
          ),
          Validators.max(
            getMaxMinValidator("DELTA_L_PRELOAD_MAX", this.unitSystem),
          ),
          Validators.pattern(
            getDecimalPattern("deltaLPreload", this.unitSystem),
          ),
        ],
      ],
    };

    // Simulation Settings controls
    const simulationSettingsControls = {
      unsuppBkl: [defaults.unsuppBkl],
      thermoCollapse: [defaults.thermoCollapse],
    };

    // LUS Coeff controls
    const lusCoeffControls = {};
    this.lusCoeffLabels.forEach((i, idx) => {
      const fieldName = "lusCoeffL" + i;
      lusCoeffControls[fieldName] = [
        this.lusCoeffDefaults[idx],
        [Validators.pattern(getDecimalPattern(fieldName, this.unitSystem))],
      ];
    });

    // Temp. °F controls
    const tempTControls = {};
    for (let t = 1; t <= 5; t++) {
      tempTControls["tempT" + t] = [
        this.tempTDefaults[t - 1],
        [Validators.pattern(getDecimalPattern("tempT" + t, this.unitSystem))],
      ];
    }

    this.fgGeothermal = this.fb.group({
      ...generalInfoControls,
      ...casingSpecControls,
      ...holeDimensionControls,
      ...loadInfoControls,
      ...displacementPreloadControls,
      ...simulationSettingsControls,
      ...lusCoeffControls,
      ...tempTControls,
    });

    // Capture initial defaults snapshot for New Simulation reset
    this.defaultFormValue = this.fgGeothermal.getRawValue();
    this.originalFormValue = this.defaultFormValue;

    this.fgGeothermal
      .get("calcEccentricity")
      ?.valueChanges.subscribe((checked: boolean) => {
        const eccentricityCtrl = this.fgGeothermal.get("eccentricity");
        if (checked) {
          eccentricityCtrl?.enable();
        } else {
          eccentricityCtrl?.disable();
          eccentricityCtrl?.setValue(null);
        }
      });
  }

  // --- Canonical units helpers ---
  private getConvertibleControls(): string[] {
    const temps = Array.from({ length: 5 }, (_, i) => `tempT${i + 1}`);
    return [
      // Lengths (in/mm)
      "outsideDiameter",
      "wallThickness",
      "insideDiameter",
      "holeId",
      "eccentricity",
      // Mass per length (lb/ft, kg/m)
      "nominalWeight",
      // Pressures (psi, MPa)
      "insidePressureInitial",
      "insidePressureFinal",
      "outsidePressureInitial",
      "outsidePressureFinal",
      // Densities (ppg, kg/m3)
      "insideDensityInitial",
      "insideDensityFinal",
      "outsideDensityInitial",
      "outsideDensityFinal",
      // Temperatures (°F, °C)
      "avgTemperatureInitial",
      "avgTemperatureFinal",
      // Mechanical length (ft, m)
      "deltaLPreload",
      // Stress-like (psi, MPa)
      "modulusE",
      "smys",
      // Thermal expansion (%/°F, %/°C)
      "thermalExp",
      "tempDerating",
      ...temps,
    ];
  }

  private getUnitForControl(
    control: string,
    system: UnitSystem,
  ): string | null {
    const USC = UnitSystem.USC;
    // Map to UnitConversionService unit symbol strings
    const from = (s: UnitSystem) => {
      switch (control) {
        case "outsideDiameter":
        case "wallThickness":
        case "insideDiameter":
        case "holeId":
        case "eccentricity":
          return s === USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;
        case "deltaLPreload":
          return s === USC ? UNITS.LENGTH_FT : UNITS.LENGTH_M;
        case "nominalWeight":
          return s === USC
            ? UNITS.MASS_PER_LENGTH_LBFT
            : UNITS.MASS_PER_LENGTH_KGM;
        case "insideDensityInitial":
        case "insideDensityFinal":
        case "outsideDensityInitial":
        case "outsideDensityFinal":
          return s === USC ? UNITS.DENSITY_PPG : UNITS.DENSITY_KGM3;
        case "insidePressureInitial":
        case "insidePressureFinal":
        case "outsidePressureInitial":
        case "outsidePressureFinal":
        case "modulusE":
        case "smys":
          return s === USC ? UNITS.PRESSURE_PSI : UNITS.PRESSURE_MPA;
        case "avgTemperatureInitial":
        case "avgTemperatureFinal":
        case "tempT1":
        case "tempT2":
        case "tempT3":
        case "tempT4":
        case "tempT5":
          return s === USC ? UNITS.TEMPERATURE_F : UNITS.TEMPERATURE_C;
        case "thermalExp":
        case "tempDerating":
          return s === USC ? UNITS.THERMAL_EXP_F : UNITS.THERMAL_EXP_C;
        default:
          return null;
      }
    };
    return from(system);
  }

  private convertValue(
    value: any,
    fromUnit: string | null,
    toUnit: string | null,
  ): number | null {
    if (value == null || value === "") return null;
    const num = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(num)) return null;
    if (!fromUnit || !toUnit || fromUnit === toUnit) return num;

    try {
      const result = this.unitService.convert(num, fromUnit, toUnit);

      return result;
    } catch (error) {
      return num;
    }
  }

  private initializeCanonicalFromForm() {
    // Ya no se usa rawByUnit, solo canonicalSI si es necesario
    if (!this.fgGeothermal) return;
    const controls = this.getConvertibleControls();
    const current = this.unitSystem;
    const siSystem = UnitSystem.SI;
    this.isProgrammaticPatch = true;
    controls.forEach((key) => {
      const ctrl = this.fgGeothermal.get(key);
      if (!ctrl) return;
      const raw = (ctrl as any)?.getRawValue
        ? (ctrl as any).getRawValue()
        : ctrl.value;
      const fromUnit = this.getUnitForControl(key, current);
      const toUnit = this.getUnitForControl(key, siSystem);
      const si = this.convertValue(raw, fromUnit, toUnit);
      this.canonicalSI[key] = si;
    });
    this.isProgrammaticPatch = false;
  }

  private setupCanonicalObservers() {
    // Unsubscribe previous
    this.canonicalSubs.forEach((s) => s.unsubscribe());
    this.canonicalSubs = [];
    const controls = this.getConvertibleControls();
    controls.forEach((key) => {
      const ctrl = this.fgGeothermal.get(key);
      if (!ctrl) return;
      const sub = ctrl.valueChanges.subscribe((val: any) => {
        if (this.isPatchingFromLoad || this.isProgrammaticPatch) return;
        const current = this.unitSystem;
        const fromUnit = this.getUnitForControl(key, current);
        const toUnit = this.getUnitForControl(key, UnitSystem.SI);
        const si = this.convertValue(val, fromUnit, toUnit);
        this.canonicalSI[key] = si;
      });
      this.canonicalSubs.push(sub);
    });
  }

  private subscribeActionService() {
    this.subActionService = this.actionService.currentMessage.subscribe(
      (value) => {
        switch (value) {
          case HeaderState.new:
            this.confirmReset();
            break;
          case HeaderState.report:
            // this.printReport();
            break;
          case HeaderState.validate:
            // this.confirmReport();
            break;
          default:
            break;
        }
      },
    );
  }

  private confirmReset(
    afterResetCallback?: () => void,
    customMessage?: string,
  ) {
    // Mirror Corrosion Rate UX: prompt if form has values; otherwise reset and inform
    const hasChanges = this.isFormModified || this.hasGeothermalResults();
    if (hasChanges) {
      Swal.fire({
        title: "Are you sure?",
        text: customMessage || "The values on screen will be deleted",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#009",
        cancelButtonColor: "#aaa",
        confirmButtonText: "Proceed",
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetSimulation();
          this.preSimulationService.resetValues();
          if (afterResetCallback) {
            afterResetCallback();
          }
        }
      });
    } else {
      this.resetSimulation();
      this.preSimulationService.resetValues();
      if (afterResetCallback) {
        afterResetCallback();
      } else {
        Swal.fire({
          title: "New simulation created",
          icon: "info",
          confirmButtonColor: "#009",
          cancelButtonColor: "#aaa",
        });
      }
    }
  }

  private resetSimulation() {
    // Clear simulation context
    this.simulationName = "";
    this.simulationRev = "" as any;
    this.currentTreeId = null;

    this.saveSimulationService.setAllSavedValues({});

    // Clear results and restore default values
    this.geothermalResults = null;
    if (this.fgGeothermal) {
      // Use centralized defaults (includes tempT and lusCoeff)
      const defaults = getGeothermalFormDefaults(this.unitSystem);
      Object.keys(this.fgGeothermal.controls).forEach((key) => {
        if (defaults.hasOwnProperty(key)) {
          this.fgGeothermal
            .get(key)
            ?.setValue(defaults[key], { emitEvent: false });
        } else {
          this.fgGeothermal.get(key)?.reset(null, { emitEvent: false });
        }
      });
      this.defaultFormValue = this.fgGeothermal.getRawValue();
      this.originalFormValue = this.defaultFormValue;
      // Reset touched/dirty to restore a clean state and validations
      Object.keys(this.fgGeothermal.controls).forEach((key) => {
        const ctrl = this.fgGeothermal.get(key);
        ctrl?.markAsPristine();
        ctrl?.markAsUntouched();
        ctrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
    }
  }

  private validateIfPreSimulationSaved(
    customerId: number | null,
    countryId: number | null,
  ): boolean {
    let isSaved = true;

    if (
      this.preSimulationService.getCurrentCountryId() == null ||
      this.preSimulationService.getCurrentCustomerId() == null ||
      this.preSimulationService.getCurrentCountryId() != countryId ||
      this.preSimulationService.getCurrentCustomerId() != customerId
    ) {
      isSaved = false;
    }

    return isSaved;
  }

  private fireWarningModal(title: string, text: string) {
    Swal.fire({
      title: title,
      text: text,
      icon: "warning",
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
    });
  }

  private handleLoadSimulation(
    selectedSimulation: any,
    resetFirst: boolean = false,
  ) {
    const treeId = selectedSimulation?.simulation?.id;
    if (treeId == null) {
      this.showWarning("Error loading simulation.");
      return;
    }

    this.currentTreeId = treeId;
    const customerName = selectedSimulation?.customer?.name ?? "";
    const regionName = selectedSimulation?.region?.name ?? "";
    const projectName = selectedSimulation?.project?.name ?? "";
    const simulationName =
      selectedSimulation?.simulation?.name ?? selectedSimulation?.path ?? "";
    this.simulationName = [
      customerName,
      regionName,
      projectName,
      simulationName,
    ]
      .filter(Boolean)
      .join("-");

    // Helper function to get fallback values - use defaults if reset was done, otherwise current form values
    const getFallbackValue = (fieldName: string) => {
      if (resetFirst && this.defaultFormValue) {
        return this.defaultFormValue[fieldName];
      }
      return this.fgGeothermal.get(fieldName)?.value;
    };

    // Helper function that respects explicit null values from server
    const getValueWithFallback = (serverValue: any, fieldName: string) => {
      // If server explicitly sends null, respect it
      if (serverValue === null) {
        return null;
      }
      // If server value exists (not undefined), use it
      if (serverValue !== undefined) {
        return serverValue;
      }
      // Only use fallback if server value is undefined (field not present)
      return getFallbackValue(fieldName);
    };

    this.geothermalService.loadSimulation(treeId).subscribe({
      next: (response) => {
        this.customerId = response?.data?.calculationData?.customerId;
        this.countryId = response?.data?.calculationData?.countryId;
        this.preSimulationService.setLoadedCustomerId(this.customerId);
        this.preSimulationService.setLoadedCountryId(this.countryId);
        this.preSimulationService.setSavedCustomerId(this.customerId);
        this.preSimulationService.setSavedCountryId(this.countryId);

        // Prevent valueChanges side-effects while patching form with loaded data
        this.isPatchingFromLoad = true;

        this.simulationRev = response.data.calculationData.rev;

        // Clear unit conversion cache to prevent stale values after load
        this.canonicalSI = {};
        const calc =
          response?.data?.calculationData ?? response?.calculationData;
        if (!calc) {
          // Graceful handling: load available metadata without failing
          // Keep current form values; clear results; mark as successfully loaded
          this.geothermalResults = null;

          // Reveal validators and finish patching flow
          setTimeout(() => {
            this.revealAllErrorsAfterLoad();
            this.isPatchingFromLoad = false;
            // Treat current state as baseline so printing gating uses saved baseline logic
            this.originalFormValue = this.fgGeothermal.getRawValue();
            // Refresh canonical/raw stores based on loaded values
            this.initializeCanonicalFromForm();
          }, 0);

          Swal.fire({
            title: "Simulation loaded",
            text: "The simulation was loaded without calculation data. Fields not provided were left unchanged.",
            icon: "info",
            confirmButtonColor: "#009",
            timer: 2500,
            timerProgressBar: true,
          });
          return;
        }

        // Determine skipConversion: true if loaded unit system matches current, false otherwise
        let loadedUnit: UnitSystem | null = null;
        if (calc.unitSystem === "SI") loadedUnit = UnitSystem.SI;
        if (calc.unitSystem === "USC" || calc.unitSystem === "US")
          loadedUnit = UnitSystem.USC;
        const skipConversion =
          loadedUnit !== null && loadedUnit === this.unitSystem;
        if (loadedUnit !== null && !skipConversion) {
          this.onUnitSystemChange(loadedUnit);
        }

        // Patch inputs para Load Information y Casing Spec
        const tc = calc.thermalCollapseInput || {};
        const ul = calc.unsupportedBucklingInput || null;
        const tcInput = calc.thermalCollapseInput || {};
        const odValue = getValueWithFallback(tc.nomOD, "outsideDiameter");
        this.fgGeothermal.patchValue(
          {
            // Casing spec
            wallThickness: getValueWithFallback(tc.nomWT, "wallThickness"),
            smys: getValueWithFallback(tc.smys, "smys"),
            nominalWeight: getValueWithFallback(
              ul?.nomWeight ?? calc.nominalWeight,
              "nominalWeight",
            ),
            grade: getValueWithFallback(calc.grade, "grade"),
            // Derating y temperaturas
            tempDerating: getValueWithFallback(
              tc.temperatureDerating,
              "tempDerating",
            ),
            avgTemperatureFinal: getValueWithFallback(
              ul?.avgTemperatureFinal,
              "avgTemperatureFinal",
            ),
          },
          { emitEvent: true },
        );
        // Campos a forzar con setTimeout+setValue para asegurar visibilidad
        const patchFields = [
          ["outsideDiameter", odValue],
          ["wallThickness", getValueWithFallback(tc.nomWT, "wallThickness")],
          ["smys", getValueWithFallback(tc.smys, "smys")],
          [
            "insideDiameter",
            (() => {
              // For insideDiameter, only set explicit values from server
              // If server doesn't provide it, let updateInsideDiameter() calculate it
              const hasExplicitValue =
                (ul && ul.hasOwnProperty("insideDiameter")) ||
                (tc && tc.hasOwnProperty("insideDiameter")) ||
                (calc && calc.hasOwnProperty("insideDiameter"));

              if (hasExplicitValue) {
                const serverValue =
                  ul?.insideDiameter ??
                  tc.insideDiameter ??
                  calc.insideDiameter;
                return getValueWithFallback(serverValue, "insideDiameter");
              }

              // If no explicit value from server, return undefined so updateInsideDiameter() can calculate it
              return undefined;
            })(),
          ],
          // For these fields, prioritize more specific sources and respect explicit null values
          [
            "nominalWeight",
            (() => {
              // If ul exists and has nomWeight explicitly set (even if null), use it
              if (ul && ul.hasOwnProperty("nomWeight")) {
                return getValueWithFallback(ul.nomWeight, "nominalWeight");
              }
              // Otherwise check other sources
              const serverValue =
                tc.nomWeight ?? tcInput.nomWeight ?? calc.nominalWeight;
              return getValueWithFallback(serverValue, "nominalWeight");
            })(),
          ],
          [
            "grade",
            (() => {
              // If ul exists and has steelGrade explicitly set (even if null/empty), use it
              if (ul && ul.hasOwnProperty("steelGrade")) {
                return getValueWithFallback(ul.steelGrade, "grade");
              }
              // Otherwise check other sources
              const serverValue =
                tc.steelGrade ?? tcInput.steelGrade ?? calc.grade;
              return getValueWithFallback(serverValue, "grade");
            })(),
          ],
          ["holeId", getValueWithFallback(ul?.holeID, "holeId")],
          // calcEccentricity: check de Eccentricity (si es null se mantiene, si es false pasa a true, si es true pasa a false)
          [
            "calcEccentricity",
            (function () {
              const val = ul?.calcEccentricity;
              if (val === null || val === undefined)
                return getValueWithFallback(val, "calcEccentricity");
              if (val === false) return true;
              if (val === true) return false;
              return getValueWithFallback(val, "calcEccentricity");
            })(),
          ],
          [
            "eccentricity",
            (function () {
              const calc = ul?.calcEccentricity;
              const val = ul?.eccentricity;
              if (!calc) return val;
              else return null;
            })(),
          ],
          [
            "tempDerating",
            getValueWithFallback(tc.temperatureDerating, "tempDerating"),
          ],
          [
            "avgTemperatureFinal",
            getValueWithFallback(
              ul?.avgTemperatureFinal,
              "avgTemperatureFinal",
            ),
          ],
          [
            "insideDensityInitial",
            getValueWithFallback(ul?.densityInInitial, "insideDensityInitial"),
          ],
          [
            "insideDensityFinal",
            getValueWithFallback(ul?.densityInFinal, "insideDensityFinal"),
          ],
          [
            "outsideDensityInitial",
            getValueWithFallback(
              ul?.densityOutInitial,
              "outsideDensityInitial",
            ),
          ],
          [
            "outsideDensityFinal",
            getValueWithFallback(ul?.densityOutFinal, "outsideDensityFinal"),
          ],
          [
            "insidePressureInitial",
            getValueWithFallback(
              ul?.pressureInInitial,
              "insidePressureInitial",
            ),
          ],
          [
            "insidePressureFinal",
            getValueWithFallback(ul?.pressureInFinal, "insidePressureFinal"),
          ],
          [
            "outsidePressureInitial",
            getValueWithFallback(
              ul?.pressureOutInitial,
              "outsidePressureInitial",
            ),
          ],
          [
            "outsidePressureFinal",
            getValueWithFallback(ul?.pressureOutFinal, "outsidePressureFinal"),
          ],
          [
            "avgTemperatureInitial",
            getValueWithFallback(
              ul?.avgTemperatureInitial,
              "avgTemperatureInitial",
            ),
          ],
          ["deltaLPreload", getValueWithFallback(ul?.preLoad, "deltaLPreload")],
          ["hollLimit", getValueWithFallback(ul?.hollidayLimit, "hollLimit")],
        ];

        setTimeout(() => {
          patchFields.forEach(([key, value]) => {
            const ctrl = this.fgGeothermal.get(key);
            if (ctrl && value !== undefined) {
              ctrl.setValue(value, { emitEvent: true });
              ctrl.updateValueAndValidity();
            }
          });
          this.cdr.detectChanges();
        }, 0);
        // Recalcular y mostrar ID tras cargar OD y WT, but respect explicit values from server
        this.updateInsideDiameter(true);
        this.cdr.detectChanges();

        if (!ul) {
          // Fallback: cargar valores por defecto
          // Use defaults if reset was done, otherwise use original form values
          const d = resetFirst
            ? this.defaultFormValue || {}
            : this.originalFormValue || {};
          const patch: any = {
            nominalWeight: d.nominalWeight ?? null,
            holeId: d.holeId ?? null,
            eccentricity: d.eccentricity ?? null,
            grade: d.grade ?? null,
            insideDensityInitial: d.insideDensityInitial ?? null,
            insideDensityFinal: d.insideDensityFinal ?? null,
            outsideDensityInitial: d.outsideDensityInitial ?? null,
            outsideDensityFinal: d.outsideDensityFinal ?? null,
            insidePressureInitial: d.insidePressureInitial ?? 0,
            insidePressureFinal: d.insidePressureFinal ?? 0,
            outsidePressureInitial: d.outsidePressureInitial ?? 0,
            outsidePressureFinal: d.outsidePressureFinal ?? 0,
            avgTemperatureInitial: d.avgTemperatureInitial ?? null,
            avgTemperatureFinal: d.avgTemperatureFinal ?? null,
            deltaLPreload: d.deltaLPreload ?? 0,
            friction: d.friction ?? 0.3,
            modulusE:
              d.modulusE ??
              (this.unitSystem === UnitSystem.USC ? 3.0e7 : 206842),
            poisson: d.poisson ?? 0.3,
            thermalExp:
              d.thermalExp ??
              (this.unitSystem === UnitSystem.USC ? "6.9e-6" : "1.24e-5"),
            hollLimit: d.hollLimit ?? null,
            calcEccentricity: d.calcEccentricity ?? false,
            directionMotion:
              d.directionMotion ?? this.directionMotionOptions[0].value,
          };

          this.fgGeothermal.patchValue(patch, { emitEvent: true });

          // LUS Coeffs por defecto
          this.lusCoeffLabels.forEach((i, idx) => {
            const key = "lusCoeffL" + i;
            const val = this.lusCoeffDefaults[idx];
            this.fgGeothermal.get(key)?.setValue(val, { emitEvent: true });
          });

          // Info: avisar que se aplicaron defaults por no traer inputs de Unsupported Length
          Swal.fire({
            icon: "info",
            title: "Defaults applied",
            text: "Unsupported Length inputs were not found in the loaded simulation. Default values were applied.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        }

        // Coeficientes de longitud no soportada (si existen controles)
        // Temperaturas discretas (tempT1..tempT5) desde tc.temperatures si están presentes
        if (Array.isArray(tc.temperatures)) {
          for (let t = 1; t <= 5; t++) {
            const val = tc.temperatures[t - 1];
            if (val != null) {
              this.fgGeothermal
                .get("tempT" + t)
                ?.setValue(val, { emitEvent: true });
            }
          }
        }
        // Coeficientes LUS si existiera el mapeo 1..12 en el formulario (lusCoeffL1..lusCoeffL12)
        if (ul && Array.isArray(ul.unsupportedLengthCoefficients_Secant)) {
          ul.unsupportedLengthCoefficients_Secant.forEach(
            (val: number, idx: number) => {
              const key = `lusCoeffL${idx + 1}`;
              this.fgGeothermal.get(key)?.setValue(val);
            },
          );
        }

        const thermalCollapseChecked = !!calc.calculateThermalCollapse;
        const unsupportedBucklingChecked = !!calc.calculateUnsupportedBuckling;
        this.fgGeothermal
          .get("thermoCollapse")
          ?.setValue(thermalCollapseChecked);
        this.fgGeothermal
          .get("unsuppBkl")
          ?.setValue(unsupportedBucklingChecked);

        // Normalize results data first, but don't set visibility flags yet
        const normalizedResults = normalizeGeothermalResults(calc);

        // Save loaded simulation context for future saves
        this.saveSimulationService.setAllSavedValues({
          customerId: this.customerId,
          regionId: selectedSimulation?.region?.id ?? null,
          projectId: selectedSimulation?.project?.id ?? null,
          simulationId: selectedSimulation?.simulation?.id ?? null,
          customerName:
            selectedSimulation?.customer?.description ??
            selectedSimulation?.customer?.name ??
            null,
          regionName:
            selectedSimulation?.region?.description ??
            selectedSimulation?.region?.name ??
            null,
          projectName:
            selectedSimulation?.project?.description ??
            selectedSimulation?.project?.name ??
            null,
          simulationName:
            selectedSimulation?.simulation?.description ??
            selectedSimulation?.simulation?.name ??
            null,
          visibility: null,
          notes: null,
        });

        const ownerUserId =
          selectedSimulation?.userId ?? selectedSimulation?.user?.id ?? null;
        const currentUserId = this.dataService.getCurrentUserId();

        if (ownerUserId && currentUserId && ownerUserId === currentUserId) {
          if (typeof calc.visibility === "string") {
            this.saveSimulationService.setSavedVisibility(calc.visibility);
          }
          if (typeof calc.notes === "string") {
            this.saveSimulationService.setSavedNotes(calc.notes);
          }
        }

        // Recalcular diámetros dependientes, but respect explicit values from server
        this.updateInsideDiameter(true);

        // Set results and visibility flags after DOM is fully rendered to ensure correct canvas sizing
        setTimeout(() => {
          // Set results first
          this.geothermalResults = normalizedResults;

          // Then set visibility flags based on form values and available data
          const thermoCtrl = this.fgGeothermal.get("thermoCollapse");
          const unsuppCtrl = this.fgGeothermal.get("unsuppBkl");
          this.showThermalCollapseResults =
            !!thermoCtrl?.value &&
            !!this.geothermalResults?.thermalCollapseResponses?.length;
          this.showUnsupportedBucklingResults =
            !!unsuppCtrl?.value &&
            !!this.geothermalResults?.unsupportedLengthResponses?.outputs
              ?.length;

          // Subscribe to checkbox changes for real-time updates
          thermoCtrl?.valueChanges.subscribe((val: any) => {
            this.showThermalCollapseResults =
              !!val &&
              !!this.geothermalResults?.thermalCollapseResponses?.length;
          });
          unsuppCtrl?.valueChanges.subscribe((val: any) => {
            this.showUnsupportedBucklingResults =
              !!val &&
              !!this.geothermalResults?.unsupportedLengthResponses?.outputs
                ?.length;
          });
        }, 0);
        // Forzar visibilidad de todos los errores al cargar, aún en modo patching
        setTimeout(() => {
          this.revealAllErrorsAfterLoad();
          // Done patching from load; re-enable valueChanges side-effects
          this.isPatchingFromLoad = false;
          // Treat loaded values as baseline (not modified) so printing is allowed
          this.originalFormValue = this.fgGeothermal.getRawValue();
          // Refresh canonical/raw stores based on loaded values
          this.initializeCanonicalFromForm();
        }, 0);

        Swal.fire({
          title: "Simulation loaded",
          icon: "success",
          confirmButtonColor: "#009",
          timer: 2000,
          timerProgressBar: true,
        });

        // Update simulation name for header modal SDCaseNumber field
        this.dataService.setSimulationIdName(this.simulationName);
      },
      error: () => {
        this.isPatchingFromLoad = false;
        this.showError();
      },
    });
  }

  private handleSaveSimulation(saveSimulationForm: SaveSimulationForm) {
    if (!saveSimulationForm.simulation.id) {
      this.saveSimulation(saveSimulationForm);
    } else {
      this.confirmOverwrite(saveSimulationForm);
    }
  }

  private confirmOverwrite(saveSimulationForm: SaveSimulationForm) {
    Swal.fire({
      title: "Do you want to overwrite the simulation",
      text: saveSimulationForm.path + " ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Proceed",
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveSimulation(saveSimulationForm);
      }
    });
  }

  private saveSimulation(saveSimulationForm: SaveSimulationForm) {
    const geothermalRequest = this.getGeothermalSaveRequest(saveSimulationForm);
    this.geothermalService
      .saveSimulation(geothermalRequest, saveSimulationForm)
      .subscribe((response) => {
        if (response.status.success) {
          this.handleSaveSuccess(
            response,
            geothermalRequest,
            saveSimulationForm,
          );
        } else {
          this.showError();
        }
      });
  }

  private handleSaveSuccess(
    response: any,
    request: any,
    saveSimulationForm: SaveSimulationForm,
  ) {
    Swal.fire({
      title: "Saved!",
      text: "The simulation has been saved.",
      icon: "success",
      confirmButtonColor: "#009",
      timer: 3000,
      timerProgressBar: true,
    });

    this.simulationRev = response.data.rev;
    this.simulationName = saveSimulationForm.path;
    this.dataService.setSimulationIdName(saveSimulationForm.path);
    this.countryId = request.countryId;
    this.customerId = request.customerId;

    // Persist current tree id, prioritizing backend response if available
    this.currentTreeId =
      response?.data?.treeId ??
      request?.treeId ??
      saveSimulationForm?.simulation?.id ??
      this.currentTreeId ??
      null;

    // Pre Simulation context
    this.preSimulationService.setSavedCustomerId(this.customerId);
    this.preSimulationService.setSavedCountryId(this.countryId);

    // Save simulation context
    this.saveSimulationService.setAllSavedValues({
      customerId: saveSimulationForm.customer?.id ?? null,
      regionId: saveSimulationForm.region?.id ?? null,
      projectId: saveSimulationForm.project?.id ?? null,
      simulationId: saveSimulationForm.simulation?.id ?? null,
      customerName: saveSimulationForm.customer?.description ?? null,
      regionName: saveSimulationForm.region?.description ?? null,
      projectName: saveSimulationForm.project?.description ?? null,
      simulationName: saveSimulationForm.simulation?.description ?? null,
      visibility: saveSimulationForm.visibility ?? null,
      notes: saveSimulationForm.notes ?? null,
    });

    // Mark current form state as the saved baseline to allow printing
    this.originalFormValue = this.fgGeothermal.getRawValue();
  }

  private showError() {
    Swal.fire({
      title: "An error occurred",
      icon: "error",
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
    });
  }

  private showWarning(message: string) {
    Swal.fire({
      title: "Action required",
      text: message,
      icon: "warning",
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
    });
  }

  ngOnDestroy(): void {
    if (this.subActionService) {
      this.subActionService.unsubscribe();
    }
    if (this.dcpProduct$) {
      this.dcpProduct$.unsubscribe();
    }
    if (this.headerSubscription) {
      this.headerSubscription.unsubscribe();
    }
    if (this.canonicalSubs?.length) {
      this.canonicalSubs.forEach((s) => s.unsubscribe());
      this.canonicalSubs = [];
    }
  }

  showRangeModal(): void {
    this.isRangeModalOpen = true;
  }

  closeRangeModal(): void {
    this.isRangeModalOpen = false;
  }

  closeSaveModal(): void {
    this.isSaveModalOpen = false;
  }

  private buildGeothermalMapping(form: any, isCalculate: boolean = false) {
    const calculateThermalCollapse = form.thermoCollapse ?? true;
    const calculateUnsupported = form.unsuppBkl ?? true;

    const thermalCollapseInput = {
      temperatureDerating: form.tempDerating,
      nomOD: form.outsideDiameter,
      nomWT: form.wallThickness,
      smys: form.smys,
      temperatures: [
        form.tempT1,
        form.tempT2,
        form.tempT3,
        form.tempT4,
        form.tempT5,
      ].filter((v) => v != null),
    };

    const unsupportedLengthCoefficients_Secant = [];
    for (let i = isCalculate ? 2 : 1; i <= 8; i++) {
      const val = form[`lusCoeffL${i}`];
      unsupportedLengthCoefficients_Secant.push(val != null ? val : 0);
    }
    const unsupportedLengthCoefficients_Euler = [];
    for (let i = 9; i <= 12; i++) {
      const val = form[`lusCoeffL${i}`];
      unsupportedLengthCoefficients_Euler.push(val != null ? val : 0);
    }

    const unsupportedInput = {
      nomOD: form.outsideDiameter,
      nomWeight: form.nominalWeight,
      nomWT: form.wallThickness,
      insideDiameter: form.insideDiameter,
      smys: form.smys,
      holeID: form.holeId,
      eccentricity: form.eccentricity ?? 0,
      calcEccentricity: !form.calcEccentricity,
      unsupportedLengthCoefficients_Secant,
      unsupportedLengthCoefficients_Euler,
      hollidayLimit: form.hollLimit,
      young: form.modulusE,
      poisson: form.poisson,
      coeffThermalExp: form.thermalExp,
      frictionFactor: form.friction,
      motion: form.directionMotion,
      densityInInitial: form.insideDensityInitial,
      densityInFinal: form.insideDensityFinal,
      densityOutInitial: form.outsideDensityInitial,
      densityOutFinal: form.outsideDensityFinal,
      pressureInInitial: form.insidePressureInitial,
      pressureInFinal: form.insidePressureFinal,
      pressureOutInitial: form.outsidePressureInitial,
      pressureOutFinal: form.outsidePressureFinal,
      avgTemperatureInitial: form.avgTemperatureInitial,
      avgTemperatureFinal: form.avgTemperatureFinal,
      preLoad: form.deltaLPreload,
      steelGrade: form.grade,
    };

    return {
      unitSystem: this.unitSystem,
      calculateThermalCollapse,
      calculateUnsupported,
      thermalCollapseInput,
      unsupportedInput,
    };
  }

  onSubmit(): void {
    if (!this.fgGeothermal.valid) {
      // Reveal and focus first invalid control for quick fixing
      this.fgGeothermal.markAllAsTouched();
      Object.keys(this.fgGeothermal.controls).forEach((key) =>
        this.fgGeothermal.get(key)?.updateValueAndValidity({ onlySelf: true }),
      );
      setTimeout(() => this.scrollToFirstInvalidControl(), 0);
      return;
    }

    if (this.fgGeothermal.valid) {
      const form = this.fgGeothermal.getRawValue();
      const mapped = this.buildGeothermalMapping(form, true);
      const request = {
        unitSystem: mapped.unitSystem,
        calculateThermalCollapse: mapped.calculateThermalCollapse,
        // Renamed per API contract: previously calculateUnsupportedLength
        calculateUnsupportedBuckling: mapped.calculateUnsupported,
        thermalCollapseInput: mapped.thermalCollapseInput,
        // Keep input name aligned with buckling terminology
        unsupportedBucklingInput: mapped.unsupportedInput,
      };

      this.geothermalResults = null;

      this.geothermalService.calculate(request).subscribe({
        next: (resp) => {
          if (
            Array.isArray(resp?.data) &&
            resp.data.length &&
            resp.data[0].code &&
            resp.data[0].message
          ) {
            this.geothermalResults = null;

            Swal.fire({
              icon: "error",
              title: "An error occurred",
              text: !environment.production
                ? resp.data[0].message
                : "An error occurred during calculation models.",
            });

            return;
          }

          const data = resp?.data ?? resp;
          this.geothermalResults = normalizeGeothermalResults(data);

          // Set show flags based on form checkboxes and successful response
          this.showThermalCollapseResults =
            !!form.thermoCollapse &&
            !!this.geothermalResults?.thermalCollapseResponses?.length;
          this.showUnsupportedBucklingResults =
            !!form.unsuppBkl &&
            !!this.geothermalResults?.unsupportedLengthResponses?.outputs
              ?.length;
        },
        error: (err) => {
          this.geothermalResults = null;

          let errorMessage = !environment.production
            ? err?.error?.message
            : "An error occurred during calculation.";

          Swal.fire({
            icon: "error",
            title: "Calculation Error",
            text: errorMessage,
          });

          console.error(errorMessage);
        },
      });
    }
  }

  loadModal() {
    // To avoid problems with the unit system changes
    const filterValues = {
      outsideDiameter: null,
      wallThickness: null,
      nominalWeight: null,
      grade: null,
      connection: null,
      minWallThickness: null,
    };

    this.slottedModalComponent.preloadFilters(
      filterValues,
      this.unitSystem === UnitSystem.USC ? UnitSystem.USC : UnitSystem.SI,
    );
  }

  private getGeothermalSaveRequest(
    saveSimulationForm?: SaveSimulationForm,
  ): any {
    const form = this.fgGeothermal.getRawValue();
    const unitSystem = this.unitSystem;
    const treeId = saveSimulationForm?.simulation.id;
    // IDs from PreSimulation header (customer/country selected in header)
    const customerId = this.preSimulationService.getCurrentCustomerId();
    const countryId = this.preSimulationService.getCurrentCountryId();
    const notes = saveSimulationForm?.notes ?? null;
    const visibility = saveSimulationForm?.visibility ?? null;
    const mapped = this.buildGeothermalMapping(form);

    // Map results to outputs
    const thermalCollapseOutput = Array.isArray(
      this.geothermalResults?.thermalCollapseResponses,
    )
      ? this.geothermalResults.thermalCollapseResponses.map((item: any) => ({
          temperature: item.temperature,
          axialForce: item.axialForce,
          collapseRating: item.collapseRating,
          smySderated: item.smySderated,
        }))
      : [];

    const unsupportedBucklingOutput = {
      outputs: Array.isArray(
        this.geothermalResults?.unsupportedLengthResponses?.outputs,
      )
        ? this.geothermalResults.unsupportedLengthResponses.outputs.map(
            (item: any) => ({
              coefficientUnsupportedBuckling:
                item.coefficientUnsupportedBuckling,
              maxCompressiveStress: item.maxCompressiveStress,
              maxDeltaT: item.maxDeltaT,
              unsupportedBuckling: item.unsupportedBuckling,
              hollidayFactor: item.hollidayFactor,
            }),
          )
        : [],
    };

    const calculationData = {
      unitSystem,
      treeId,
      customerId,
      countryId,
      notes,
      visibility,
      calculateThermalCollapse: mapped.calculateThermalCollapse,
      calculateUnsupportedBuckling: mapped.calculateUnsupported,
      thermalCollapseInput: mapped.thermalCollapseInput,
      unsupportedBucklingInput: mapped.unsupportedInput,
      thermalCollapseOutput,
      unsupportedBucklingOutput,
    };

    this.customerId = customerId;
    this.countryId = countryId;

    return calculationData;
  }

  private getGeothermalPdfPrintRequest(printingModalData: any): any {
    const form = this.fgGeothermal.getRawValue();
    const unitSystem = this.unitSystem;
    const treeId = this.currentTreeId;
    const mapped = this.buildGeothermalMapping(form);
    const createdBy = this.normalizeCreatedBy(
      this.dataService.getCurrentNameSurname() ?? this.nameCurrentUser,
    );

    // Map results to outputs
    const thermalCollapseOutput = Array.isArray(
      this.geothermalResults?.thermalCollapseResponses,
    )
      ? this.geothermalResults.thermalCollapseResponses.map((item: any) => ({
          temperature: item.temperature,
          axialForce: item.axialForce,
          collapseRating: item.collapseRating,
          smySderated: item.smySderated,
        }))
      : [];

    const unsupportedBucklingOutput = {
      outputs: Array.isArray(
        this.geothermalResults?.unsupportedLengthResponses?.outputs,
      )
        ? this.geothermalResults.unsupportedLengthResponses.outputs.map(
            (item: any) => ({
              coefficientUnsupportedBuckling:
                item.coefficientUnsupportedBuckling,
              maxCompressiveStress: item.maxCompressiveStress,
              maxDeltaT: item.maxDeltaT,
              unsupportedBuckling: item.unsupportedBuckling,
              hollidayFactor: item.hollidayFactor,
            }),
          )
        : [],
    };

    const calculationData = {
      unitSystem,
      treeId,
      calculateThermalCollapse: mapped.calculateThermalCollapse,
      calculateUnsupportedBuckling: mapped.calculateUnsupported,
      thermalCollapseInput: mapped.thermalCollapseInput,
      unsupportedBucklingInput: mapped.unsupportedInput,
      thermalCollapseOutput,
      unsupportedBucklingOutput,
    };

    this.customerId = this.preSimulationService.getCurrentCustomerId();
    this.countryId = this.preSimulationService.getCurrentCountryId();

    return {
      header: {
        createdBy: createdBy,
        modelVer: this.simulationRev,
        requestedBy: printingModalData.requestedBy,
        authorizedBy: printingModalData.authorizedBy,
        reviewedBy: printingModalData.reviewedBy,
        SDCaseNumber: printingModalData.SDCaseNumber,
      },
      calculationData,
    };
  }

  private normalizeCreatedBy(val: any): string {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.filter(Boolean).join(" ").trim();
    try {
      return String(val);
    } catch {
      return "";
    }
  }

  revealFirstError(): void {
    if (!this.fgGeothermal) return;
    this.fgGeothermal.markAllAsTouched();
    Object.keys(this.fgGeothermal.controls).forEach((key) =>
      this.fgGeothermal.get(key)?.updateValueAndValidity({ onlySelf: true }),
    );
    setTimeout(() => this.scrollToFirstInvalidControl(), 0);
  }

  private revealAllErrorsAfterLoad(): void {
    if (!this.fgGeothermal) return;
    this.fgGeothermal.markAllAsTouched();
    Object.keys(this.fgGeothermal.controls).forEach((key) => {
      const ctrl = this.fgGeothermal.get(key);
      ctrl?.markAsDirty({ onlySelf: true });
      ctrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    });
    this.fgGeothermal.updateValueAndValidity({
      onlySelf: false,
      emitEvent: true,
    });
  }

  private scrollToFirstInvalidControl(): void {
    const formEl: HTMLElement | undefined = this.geoFormElem?.nativeElement;
    if (!formEl) return;
    const firstInvalid = formEl.querySelector(
      "input.ng-invalid, select.ng-invalid, textarea.ng-invalid, ng-select.ng-invalid",
    ) as HTMLElement | null;
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable =
        firstInvalid.tagName === "INPUT" ||
        firstInvalid.tagName === "SELECT" ||
        firstInvalid.tagName === "TEXTAREA"
          ? firstInvalid
          : (firstInvalid.querySelector(
              "input, select, textarea",
            ) as HTMLElement | null);
      focusable?.focus();
    }
  }

  refreshSimulationContext() {
    // Ensure the simulation code and related info are up-to-date for the Report Detail modal
    // This method should be called before opening/printing the report modal
    // Update simulationName, simulationRev, and any other fields needed for the modal
    if (this.fgGeothermal && this.originalFormValue) {
      // If the form was modified, update the simulationName and simulationRev from the latest form
      const form = this.fgGeothermal.getRawValue();
      // Optionally, update simulationName from the form or other context if needed
      // If simulationName is derived from form fields, update here
      // For now, ensure simulationRev is up-to-date if available
      if (form && form.simulationCode) {
        this.simulationName = form.simulationCode;
      }
    }
    // If simulationRev is available from the latest save/load, keep it in sync
    // (No-op if already up-to-date)
    // If you need to update more fields for the modal, do it here
  }

  printPdfReport(printingModalData: any) {
    let printedGeothermalRequest: any = this.getGeothermalPdfPrintRequest(
      this.actionService.data,
    );
    this.pdfService.getPdfGeothermal(printedGeothermalRequest);
  }

  printCsvReport() {
    const geothermalRequest = {
        "calculationData": this.getGeothermalSaveRequest(),
        "userName": this.dataService.getCurrentNameSurname() ?? null,
        "rev": this.simulationRev ?? null
    };
    this.pdfService.getCsvGeothermal(geothermalRequest);
  }

  canPrintReport(): boolean {
    if (!this.preSimulationService.getFormLoadedValidity()) {
      this.fireWarningModal(
        "Action required",
        "Please select customer and country to download the report",
      );
      return false;
    }

    if (!this.isFormValidAndComplete()) {
      this.fireWarningModal(
        "Action required",
        "Please verify all values to print report",
      );
      return false;
    }

    if (!this.isSimulationSaved()) {
      this.fireWarningModal(
        "Action required",
        "Please save simulation to print report",
      );
      return false;
    }

    // If all validations pass
    return true;
  }

  /**
   * Caches and converts values by attribute, storing both directions.
   * If the value is already cached, it returns it. If not, it clears the old cache,
   * performs the conversion, and stores both directions.
   * @param attrName Attribute name (string)
   * @param currentValue Current value (number|string)
   * @param unitSystem Target unit system
   * @param getConvertedValueFn Conversion function: (value, unitSystem) => valueConverted
   * @returns Converted value
   */
  getOrConvertValue(
    attrName: string,
    currentValue: any,
    unitSystem: UnitSystem,
    getConvertedValueFn: (
      value: any,
      unitSystem: UnitSystem,
      attrName?: string,
    ) => any,
    skipConversion: boolean = false,
  ): any {
    if (skipConversion) {
      return currentValue;
    }
    if (!this.rawByAttrAndValue) {
      this.rawByAttrAndValue = {};
    }
    if (!this.rawByAttrAndValue[attrName]) {
      this.rawByAttrAndValue[attrName] = {};
    }
    // Detect if the original value is in scientific notation
    const isScientific =
      typeof currentValue === "string" && /e[-+]?\d+/i.test(currentValue);
    // If the value is not cached
    if (this.rawByAttrAndValue[attrName][currentValue] == null) {
      // Clear old conversions
      this.rawByAttrAndValue[attrName] = {};
      // Perform the conversion, also passing the attribute name
      let valueConverted = getConvertedValueFn(
        currentValue,
        unitSystem,
        attrName,
      );
      // If the original value was in scientific notation and the converted value is numeric, format as scientific notation string
      if (isScientific && typeof valueConverted === "number") {
        valueConverted = valueConverted.toExponential(2);
      } else if (typeof valueConverted === "number") {
        if (
          GEOTHERMAL_DECIMAL_PLACES_VALIDATORS[attrName] &&
          GEOTHERMAL_DECIMAL_PLACES_VALIDATORS[attrName][unitSystem] != null
        ) {
          // If not scientific notation, apply rounding according to the validator
          const decimals =
            GEOTHERMAL_DECIMAL_PLACES_VALIDATORS[attrName][unitSystem];
          valueConverted = valueConverted.toFixed(decimals);
        }
      }
      // Store both directions
      this.rawByAttrAndValue[attrName][currentValue] = valueConverted;
      this.rawByAttrAndValue[attrName][valueConverted] = currentValue;
    }
    return this.rawByAttrAndValue[attrName][currentValue];
  }

  get shouldShowResults(): boolean {
    if (!this.geothermalResults) return false;
    const tc = this.geothermalResults.thermalCollapseResponses;
    const ub = this.geothermalResults.unsupportedLengthResponses;
    return (
      (Array.isArray(tc) && tc.length > 0) ||
      (ub &&
        ub.outputs &&
        (Array.isArray(ub.outputs)
          ? ub.outputs.length > 0
          : Object.keys(ub.outputs).length > 0))
    );
  }

  getKeysCount(obj: any): number {
    return obj ? Object.keys(obj).length : 0;
  }

  private isFormValidAndComplete(): boolean {
    return (
      this.fgGeothermal.valid &&
      this.hasGeothermalResults() &&
      this.hasNoInputErrors() &&
      this.hasNoOutputSolutionErrors()
    );
  }

  private hasGeothermalResults(): boolean {
    if (!this.geothermalResults) return false;
    const hasThermal =
      Array.isArray(this.geothermalResults.thermalCollapseResponses) &&
      this.geothermalResults.thermalCollapseResponses.length > 0;
    const hasUnsupported =
      this.geothermalResults.unsupportedLengthResponses != null &&
      ((Array.isArray(
        this.geothermalResults.unsupportedLengthResponses.outputs,
      ) &&
        this.geothermalResults.unsupportedLengthResponses.outputs.length > 0) ||
        (Array.isArray(
          this.geothermalResults.unsupportedLengthResponses.inputsErrors,
        ) &&
          this.geothermalResults.unsupportedLengthResponses.inputsErrors
            .length > 0));
    return hasThermal || hasUnsupported;
  }

  private hasNoInputErrors(): boolean {
    const unsupportedErrors =
      this.geothermalResults?.unsupportedLengthResponses?.inputsErrors;
    const thermalErrors =
      this.geothermalResults?.thermalCollapseResponses?.inputsErrors;
    const hasUnsupportedErrors =
      Array.isArray(unsupportedErrors) && unsupportedErrors.length > 0;
    const hasThermalErrors =
      Array.isArray(thermalErrors) && thermalErrors.length > 0;
    return !hasUnsupportedErrors && !hasThermalErrors;
  }

  private hasNoOutputSolutionErrors(): boolean {
    const unsupportedOutputs =
      this.geothermalResults.unsupportedLengthResponses?.outputs;
    const thermalOutputs = this.geothermalResults.thermalCollapseResponses;
    const hasUnsupportedOutputErrors = Array.isArray(unsupportedOutputs)
      ? unsupportedOutputs.some((out) => out?.solutionError != null)
      : false;
    const hasThermalOutputErrors = Array.isArray(thermalOutputs)
      ? thermalOutputs.some((out) => out?.solutionError != null)
      : false;
    return !hasUnsupportedOutputErrors && !hasThermalOutputErrors;
  }

  private isSimulationSaved(): boolean {
    return (
      !this.isFormModified &&
      this.validateIfPreSimulationSaved(this.customerId, this.countryId)
    );
  }

  private get isFormModified(): boolean {
    return (
      JSON.stringify(this.fgGeothermal.getRawValue()) !==
      JSON.stringify(this.originalFormValue)
    );
  }

  // Save gating: prevent saving when there are input or output solution errors
  private canSaveSimulation(): boolean {
    const inputsErrors =
      this.geothermalResults?.unsupportedLengthResponses?.inputsErrors;
    const hasInputErrors =
      Array.isArray(inputsErrors) && inputsErrors.length > 0;

    const outputs = this.geothermalResults?.unsupportedLengthResponses?.outputs;
    const hasOutputErrors = Array.isArray(outputs)
      ? outputs.some((o) => o?.solutionError != null)
      : false; // Allow saving without results

    if (hasInputErrors || hasOutputErrors) {
      this.fireWarningModal(
        "Action required",
        "Please fix calculation errors before saving the simulation",
      );
      return false;
    }

    return true;
  }
}
